/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { Chalk } from "chalk";
import stripAnsi from "strip-ansi";

const chalk = new Chalk({ level: 1 });

// Precompiled regexes for performance
const NEWLINE_REGEX = /\n/g;
const ANSI_UNESCAPE_REGEX = /\\u001b/g;

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogEntry {
	time: string;
	level: LogLevel;
	msg: string;
	data?: any;
}

// Object pool for LogEntry objects to reduce GC pressure in high-frequency scenarios
class LogEntryPool {
	private pool: LogEntry[] = [];
	private readonly maxPoolSize = 50; // Reasonable limit to avoid memory bloat

	get(): LogEntry {
		return this.pool.pop() || { time: "", level: "info", msg: "" };
	}

	release(entry: LogEntry): void {
		if (this.pool.length < this.maxPoolSize) {
			// Clear the object properties
			entry.time = "";
			entry.level = "info";
			entry.msg = "";
			delete entry.data;
			// Clear any dynamic properties
			Object.keys(entry).forEach((key) => {
				if (!["time", "level", "msg"].includes(key)) {
					delete (entry as any)[key];
				}
			});
			this.pool.push(entry);
		}
	}
}

const logEntryPool = new LogEntryPool();

const levelColors = {
	debug: chalk.gray,
	info: chalk.white,
	warn: chalk.yellow,
	error: chalk.red,
};

// Cap serialized error strings so one error can't produce a multi-MB log line.
// Loki drops whole lines that exceed its max_line_size, which would lose the
// very error we're trying to surface — truncation is strictly safer.
const MAX_STACK_LENGTH = 8192;
const MAX_MESSAGE_LENGTH = 4096;
// Bound the `cause` chain so a deeply-wrapped error can't blow up log volume.
const MAX_ERROR_CAUSE_DEPTH = 4;

/**
 * Convert an Error into a plain, serializable object using an explicit
 * ALLOWLIST of fields.
 *
 * `Error.prototype.message` and `.stack` are non-enumerable, so a naive
 * `Object.entries(error)` / `{ ...error }` drops them and only surfaces
 * enumerable own props (e.g. Next.js's `digest`). That is why render errors
 * previously logged as a bare `" ⨯"` marker with no message or stack.
 *
 * We deliberately do NOT copy arbitrary own properties: fetch/GraphQL/DB/auth
 * errors routinely attach request configs, tokens, cookies and full response
 * bodies, and this logger runs on every site's error path — a wholesale dump
 * would leak secrets/PII into logs. `message`/`stack` are length-capped, direct
 * `cause` chains are depth-bounded, and a non-Error `cause` is coerced to a
 * bounded string (never serialized wholesale) so it can't smuggle secrets past
 * the allowlist — keeping error logs single-line and Loki-ingestible.
 */
function errorToLogObject(error: Error, depth = 0): Record<string, unknown> {
	const isProduction = process.env.NODE_ENV === "production";
	// Slice first, then strip ANSI, so a pathologically large value is only
	// scanned up to the cap — and Next code-frame colours don't leak into prod.
	const clean = (value: string | undefined, max: number): string | undefined => {
		if (value === undefined) {
			return undefined;
		}
		const capped = value.slice(0, max);
		return isProduction ? stripAnsi(capped) : capped;
	};

	// Coerce an arbitrary value to something safe to log: primitives pass through
	// (strings bounded); objects/functions collapse to a bounded, guarded type
	// tag (e.g. `[Response]`) so their contents — which may hold secrets — never
	// reach the output. Used for `cause`, `digest`, and `code`, all of which can
	// be attacker-influenced or library-attached structured values.
	const toSafeValue = (value: unknown): unknown => {
		const type = typeof value;
		if (type === "string") {
			return clean(value as string, MAX_MESSAGE_LENGTH);
		}
		if (type === "number" || type === "boolean" || value === null) {
			return value;
		}
		if (type === "bigint" || type === "symbol") {
			return String(value).slice(0, MAX_MESSAGE_LENGTH);
		}
		// object / function: record only a type tag, guarded against throwing getters.
		try {
			const name = (value as { constructor?: { name?: unknown } })?.constructor?.name;
			const tag = typeof name === "string" ? name : type === "function" ? "Function" : "Object";
			return `[${tag.slice(0, 64)}]`;
		} catch {
			return "[Object]";
		}
	};

	const result: Record<string, unknown> = {
		name: error.name,
		message: clean(error.message, MAX_MESSAGE_LENGTH),
		stack: clean(error.stack, MAX_STACK_LENGTH),
	};

	// Non-sensitive diagnostic fields: Next render errors carry `digest`, Node
	// system errors carry `code`. Coerced in case a library attaches a structured
	// object under either name.
	const { digest, code } = error as { digest?: unknown; code?: unknown };
	if (digest !== undefined) {
		result.digest = toSafeValue(digest);
	}
	if (code !== undefined) {
		result.code = toSafeValue(code);
	}

	if (error.cause !== undefined && depth < MAX_ERROR_CAUSE_DEPTH) {
		// Recurse (allowlisted) for an Error cause; everything else is coerced to a
		// safe scalar/type-tag so a non-Error cause can't smuggle secrets past the
		// allowlist.
		result.cause = error.cause instanceof Error ? errorToLogObject(error.cause, depth + 1) : toSafeValue(error.cause);
	}

	return result;
}

/**
 * Process string values to remove newlines while preserving other whitespace
 * @param value - The value to process
 * @param seen - WeakSet to track visited objects for circular reference detection
 * @returns Processed value with newlines replaced by spaces
 */
function processStringValue(value: any, seen: WeakSet<object> = new WeakSet()): any {
	if (typeof value === "string") {
		return value.replace(NEWLINE_REGEX, " ");
	}
	// Errors must be handled before the generic object branch: their key fields
	// are non-enumerable and would otherwise be lost. (A direct `cause` is
	// already flattened by errorToLogObject; only an Error nested inside a
	// non-Error value re-enters here.) Circular chains are caught via `seen`.
	if (value instanceof Error) {
		if (seen.has(value)) {
			return "[Circular Reference]";
		}
		seen.add(value);
		return processStringValue(errorToLogObject(value), seen);
	}
	if (typeof value === "object" && value !== null) {
		// Handle circular references
		if (seen.has(value)) {
			return "[Circular Reference]";
		}
		seen.add(value);

		const processed: any = {};
		for (const [key, val] of Object.entries(value)) {
			processed[key] = processStringValue(val, seen);
		}
		return processed;
	}
	return value;
}

/**
 * Process JSON string for environment-specific ANSI handling
 * @param jsonString - The JSON string to process
 * @returns Processed string with appropriate ANSI handling
 */
function processAnsiCodes(jsonString: string): string {
	const isProduction = process.env.NODE_ENV === "production";

	// Only remove newlines to keep logs on single lines, preserve all other content spacing
	let processed = jsonString.replace(NEWLINE_REGEX, "");

	if (!isProduction) {
		// Development: Unescape ANSI codes so they render as colors
		processed = processed.replace(ANSI_UNESCAPE_REGEX, "\u001b");
	}

	return processed;
}

/**
 * Reorder object keys according to logging priority:
 * 1. time, 2. level, 3. corrId, 4. userIp, 5. msg, then alphabetically
 */
function reorderLogKeys(obj: any): any {
	if (typeof obj !== "object" || obj === null) {
		return obj;
	}

	const priorityKeys = ["time", "level", "corrId", "userIp", "msg"];
	const orderedObj: any = {};

	// Add priority keys first (if they exist)
	for (const key of priorityKeys) {
		if (key in obj) {
			orderedObj[key] = obj[key];
		}
	}

	// Add remaining keys in alphabetical order (excluding keys already added)
	const remainingKeys = Object.keys(obj)
		.filter((key) => !priorityKeys.includes(key))
		.sort();

	for (const key of remainingKeys) {
		orderedObj[key] = obj[key];
	}

	return orderedObj;
}

function safeStringify(obj: any): string {
	try {
		const orderedObj = reorderLogKeys(obj);
		const result = JSON.stringify(orderedObj);
		return processAnsiCodes(result);
	} catch (error) {
		// Handle circular references and other JSON.stringify errors
		try {
			// Attempt to stringify with a replacer that handles circular references
			const seen = new WeakSet();
			const orderedObj = reorderLogKeys(obj);
			const result = JSON.stringify(orderedObj, (_key, value) => {
				if (typeof value === "object" && value !== null) {
					if (seen.has(value)) {
						return "[Circular Reference]";
					}
					seen.add(value);
				}
				// Handle BigInt
				if (typeof value === "bigint") {
					return value.toString() + "n";
				}
				return value;
			});

			return processAnsiCodes(result);
		} catch (fallbackError) {
			// If all else fails, return a safe error message
			const isProduction = process.env.NODE_ENV === "production";
			const fallbackObj = {
				time: obj.time || new Date().toISOString(),
				level: obj.level || "Unknown",
				error: "Failed to serialize log entry",
				msg: obj.msg || "Unknown",
			};

			// Strip ANSI codes from fallback message in production
			if (isProduction && fallbackObj.msg !== "Unknown") {
				fallbackObj.msg = stripAnsi(fallbackObj.msg);
			}

			const result = JSON.stringify(reorderLogKeys(fallbackObj));
			return processAnsiCodes(result);
		}
	}
}

function log(level: LogLevel, message: unknown, data: any[] = []): void {
	const colorFn = levelColors[level];
	const isProduction = process.env.NODE_ENV === "production";

	// Convert non-string messages to string (handles Error objects, etc.)
	const messageStr =
		typeof message === "string" ? message : message instanceof Error ? message.message : String(message ?? "");

	// Get pooled entry for better performance in high-frequency scenarios
	const entry = logEntryPool.get();
	entry.time = new Date().toISOString();
	entry.level = level;
	// Strip ANSI codes in production and remove newlines from message content
	if (isProduction) {
		entry.msg = stripAnsi(messageStr).replace(NEWLINE_REGEX, " ");
	} else {
		entry.msg = messageStr.replace(NEWLINE_REGEX, " ");
	}

	// When an Error is the primary argument (e.g. `console.error(err)`), `msg`
	// holds only its message; surface `name`/`message`/`stack`/`digest`/`cause`
	// too so the log is actionable. We keep the `message` field (even though it
	// duplicates `msg` here) so an Error logged either way — sole-arg or
	// `console.error(prefix, err)` — has the same queryable shape in Loki.
	// Explicit `data` (merged below) can still override.
	if (message instanceof Error) {
		Object.assign(entry, processStringValue(message) as Record<string, unknown>);
	}

	// Process additional data if present
	if (data !== undefined && data.length) {
		const processedData = data.reduce(
			(prev, curr) => {
				if (typeof curr === "object" && curr !== null) {
					// Process nested object values to remove newlines
					const processedObj = processStringValue(curr);
					return {
						...prev,
						...processedObj,
					};
				}
				// Process primitive values to remove newlines
				prev.args.push(processStringValue(curr));
				return prev;
			},
			{
				args: [] as any[],
			},
		);

		// Only include args field if it has content
		if (processedData.args.length === 0) {
			delete processedData.args;
		}

		// Assign processed data properties to entry
		Object.assign(entry, processedData);
	}

	if (process.env.NEXT_RUNTIME === "nodejs") {
		const jsonString = safeStringify(entry);
		const isProduction = process.env.NODE_ENV === "production";

		// Only apply colors in development to avoid breaking log parsing in production
		const output = isProduction ? jsonString : colorFn(jsonString);
		process.stdout.write(output + "\n");

		// Return entry to pool for reuse
		logEntryPool.release(entry);
	} else {
		console.log(...[message, ...data]);
		// Return entry to pool for reuse
		logEntryPool.release(entry);
	}
}

export const logger = {
	debug: (message: unknown, ...data: any[]): void => log("debug", message, data),
	info: (message: unknown, ...data: any[]): void => log("info", message, data),
	log: (message: unknown, ...data: any[]): void => log("info", message, data),
	warn: (message: unknown, ...data: any[]): void => log("warn", message, data),
	error: (message: unknown, ...data: any[]): void => log("error", message, data),
};

// Export pool for potential monitoring/debugging (optional)
export const _internal = {
	logEntryPool,
	// Get current pool size for monitoring
	getPoolSize: () => logEntryPool["pool"].length,
} as const;

export const instrumentation = () => {
	const getLogMethodConsole = (consoleMethod: string) => {
		switch (consoleMethod) {
			case "error":
				return logger.error.bind(logger);
			case "warn":
				return logger.warn.bind(logger);
			case "debug":
			case "trace":
				return logger.debug.bind(logger);
			default:
				return logger.info.bind(logger);
		}
	};

	const consoleMethods = ["log", "debug", "info", "warn", "error", "trace"] as (keyof Console)[];

	consoleMethods.forEach((method) => {
		(console as any)[method] = getLogMethodConsole(method);
	});
};
